import { getErrorMessage } from "@brains/utils";
import type { EntityPluginContext, ToolContext } from "@brains/plugins";
import { parseMarkdownWithFrontmatter } from "@brains/plugins";
import type { Logger } from "@brains/utils";
import type { BlogPost } from "../schemas/blog-post";
import { blogPostFrontmatterSchema } from "../schemas/blog-post";
import { blogPostAdapter } from "../adapters/blog-post-adapter";

export function registerWithPublishPipeline(
  context: EntityPluginContext,
  logger: Logger,
): void {
  const internalProvider = {
    name: "internal",
    publish: async (): Promise<{ id: string }> => {
      return { id: "internal" };
    },
  };

  context.messaging.subscribe("system:plugins:ready", async () => {
    await context.messaging.send({
      type: "publish:register",
      payload: {
        entityType: "post",
        provider: internalProvider,
        config: { executionMode: "provider" },
      },
    });

    logger.info("Registered post with publish-pipeline");
    return { success: true };
  });
}

export function subscribeToPublishExecute(
  context: EntityPluginContext,
  logger: Logger,
): void {
  context.messaging.subscribe<
    {
      entityType: string;
      entityId: string;
      authContext?: {
        userPermissionLevel?: ToolContext["userPermissionLevel"];
      };
    },
    { success: boolean }
  >("publish:execute", async (msg) => {
    const { entityType, entityId, authContext } = msg.payload;

    if (entityType !== "post") {
      return { success: true };
    }

    try {
      context.permissions.assertEntityActionAllowed(
        entityType,
        "publish",
        authContext ?? { userPermissionLevel: "anchor" },
      );
      const post = await context.entityService.getEntity<BlogPost>({
        entityType: "post",
        id: entityId,
      });

      if (!post) {
        await context.messaging.send({
          type: "publish:report:failure",
          payload: {
            entityType,
            entityId,
            error: `Post not found: ${entityId}`,
          },
        });
        return { success: true };
      }

      if (post.metadata.status === "published") {
        logger.debug(`Post already published: ${entityId}`);
        return { success: true };
      }

      const parsed = parseMarkdownWithFrontmatter(
        post.content,
        blogPostFrontmatterSchema,
      );

      const publishedAt = new Date().toISOString();
      const updatedFrontmatter = {
        ...parsed.metadata,
        status: "published" as const,
        publishedAt,
      };

      const updatedContent = blogPostAdapter.createPostContent(
        updatedFrontmatter,
        parsed.content,
      );

      await context.entityService.updateEntity({
        entity: {
          ...post,
          content: updatedContent,
          metadata: {
            ...post.metadata,
            status: "published",
            publishedAt,
          },
        },
      });

      await context.messaging.send({
        type: "publish:report:success",
        payload: {
          entityType,
          entityId,
          result: { id: entityId },
        },
      });

      logger.info(`Published post: ${entityId}`);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await context.messaging.send({
        type: "publish:report:failure",
        payload: {
          entityType,
          entityId,
          error: errorMessage,
        },
      });
      logger.error(`Failed to publish post: ${errorMessage}`);
    }

    return { success: true };
  });

  logger.debug("Subscribed to publish:execute messages");
}
